import React, { useState, useEffect, useContext } from "react";
import { FiSearch } from "react-icons/fi";
import { BiEditAlt } from "react-icons/bi";
import { FaRegTrashCan, FaGripLines } from "react-icons/fa6";
import { IoClose } from "react-icons/io5";
import { RiImageAddLine } from "react-icons/ri";
import { LuPlus } from "react-icons/lu";

import LoadingSpinner from "../../../../../libs/LoadingSpinner";
import { toast } from "react-toastify";
import Pagination from "../../../../../libs/Pagination";
import { addSectionOfHome as addImage, deleteSectionOfHome as deleteImage, getSectionsOfHome, updateSectionOfHome as updateImage, updateSectionSequence as updateSequence } from "../../../../../libs/api/sectionOfHomeApi";
import { AppContext } from "../../../../../context/appContext";
import InternalLinkBuilder from "../../../../../libs/components/InternalLinkBuilder";

export default function Home() {
  const { t, formatNum, isRTL } = useContext(AppContext);
  const [images, setImages] = useState([]);
  const [coverImage, setCoverImage] = useState(null);
  const [coverImageUrl, setCoverImageUrl] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [modalMode, setModalMode] = useState("add");
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingAddForm, setLoadingAddForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [errors, setErrors] = useState({ image: "" });
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  /**
   * Helper to extract file name from a URL or File object.
   */
  const getFileName = (file) => {
    if (!file) return "-";
    if (typeof file === "string") return file.split("/").pop();
    if (file?.name) return file.name;
    return "-";
  };

  useEffect(() => {
    fetchImages(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, currentPage, itemsPerPage]);

  const fetchImages = async (isLoading) => {
    setLoading(isLoading);
    try {
      const response = await getSectionsOfHome({ currentPage, limit: itemsPerPage });
      if (response.status === 200) {
        // Add sequence number to each image for drag and drop
        const imagesWithSequence = response.data.data.map((image, index) => ({
          ...image,
          sequence: image.sequence || index + 1
        }));
        setImages(imagesWithSequence);
        setTotalPages(response.data.pagination?.totalPages || 0);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setLoading(false);
    }
  };

  const validateImage = (image) => {
    if (!image) {
      return t('home.imageRequired');
    }
    return "";
  };

  const handleSubmit = async () => {
    const imageError = validateImage(coverImage);
    
    if (imageError) {
      setErrors({ image: imageError });
      return;
    }
    
    // Validate file size (25MB = 25 * 1024 * 1024 bytes)
    if (coverImage.size > 25 * 1024 * 1024) {
      toast.error(t('home.imageTooLarge'));
      return;
    }
    
    setLoadingAddForm(true);
    try {
      const formData = new FormData();
      formData.append('image', coverImage);
      formData.append('link', imageUrl); // Advertisement model uses 'link' not 'url'
      const response = await addImage(formData);
      if (response.status === 200 || response.status === 201) {
        toast.success(response.data.message);
        document.getElementById("image_modal").close();
        clearForm();
        fetchImages(false);
      } else {
        toast.error(
          response.response?.data?.message || response.message || t('messages.somethingWentWrong')
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setLoadingAddForm(false);
    }
  };

  const handleUpdate = async () => {

    // const imageError = validateImage(coverImage);
    
    // if (imageError) {
    //   setErrors({ image: imageError });
    //   return;
    // }
    
    // // Validate file size if a new image is selected (25MB = 25 * 1024 * 1024 bytes)
    // if (coverImage && coverImage.size > 25 * 1024 * 1024) {
    //   toast.error("Image size must be less than 25MB");
    //   return;
    // }
    
    setLoadingAddForm(true);
    try {
      const formData = new FormData();
      if(coverImage){
        formData.append('image', coverImage);
      }
      if(imageUrl){
        formData.append('link', imageUrl); // Advertisement model uses 'link' not 'url'
      }
      const response = await updateImage(selectedImageId, formData);
      if (response.status === 200 || response.status === 201) {
        toast.success(response.data.message);
        document.getElementById("image_modal").close();
        clearForm();
        fetchImages(false);
      } else {
        toast.error(
          response.response?.data?.message || response.message || t('messages.somethingWentWrong')
        );
      }
    } catch (error) {
      toast.error(error.response?.data?.message || t('messages.somethingWentWrong'));
    } finally {
      setLoadingAddForm(false);
    }
  };

  const handleDelete = async (id) => {
      try {
        const response = await deleteImage(id);
        if (response.status === 200 || response.status === 201) {
          toast.success(response.data.message);
          fetchImages(false);
        } else {
          toast.error(
            response.response?.data?.message || response.message || t('messages.somethingWentWrong')
          );
        }
      } catch (error) {
        toast.error(error.response?.data?.message || t('messages.somethingWentWrong'));
      }
  };

  const clearForm = () => {
    setCoverImage(null);
    setCoverImageUrl(null);
    setImageUrl("");
    setSelectedImageId(null);
    setErrors({ image: "" });
  };

  const handleLinkChange = (link, metadata) => {
    setImageUrl(link);
  };

  const openModal = (mode, image = null) => {
    setModalMode(mode);
    clearForm();
    if (mode === "edit" && image) {
      setSelectedImageId(image._id);
      setCoverImageUrl(image.image); // Show existing image URL for preview
      setImageUrl(image.link || ""); // Advertisement model uses 'link' field
    }
    document.getElementById("image_modal").showModal();
  };

  const handleDragStart = (e, index) => {
    setDraggedItem(images[index]);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.target.parentNode);

    // Create a custom drag image
    const draggedElement = e.target;
    const rect = draggedElement.getBoundingClientRect();
    const dragImage = draggedElement.cloneNode(true);

    // Style the drag image
    dragImage.style.width = rect.width + 'px';
    dragImage.style.height = rect.height + 'px';
    dragImage.style.opacity = '0.8';
    dragImage.style.transform = 'rotate(5deg)';
    dragImage.style.boxShadow = '0 4px 8px rgba(0,0,0,0.2)';
    dragImage.style.backgroundColor = 'white';
    dragImage.style.border = '2px solid #0D6EFD';
    dragImage.style.borderRadius = '8px';
    dragImage.style.zIndex = '9999';

    // Hide the original element during drag
    draggedElement.style.opacity = '0.3';

    // Set the custom drag image
    e.dataTransfer.setDragImage(dragImage, rect.width / 2, rect.height / 2);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverItem(images[index]);
  };

  const handleDrop = async (e, index) => {
    e.preventDefault();
    const draggedOverItem = images[index];

    if (draggedItem === draggedOverItem) {
      setDraggedItem(null);
      setDragOverItem(null);
      return;
    }

    // Swap sequences between the two items
    const draggedSequence = draggedItem.sequence;
    const targetSequence = draggedOverItem.sequence;

    const updatedImages = images.map(image => {
      if (image._id === draggedItem._id) {
        return { ...image, sequence: targetSequence };
      }
      if (image._id === draggedOverItem._id) {
        return { ...image, sequence: draggedSequence };
      }
      return image;
    });

    // Sort by sequence to reflect the swap
    updatedImages.sort((a, b) => a.sequence - b.sequence);

    setImages(updatedImages);
    setDraggedItem(null);
    setDragOverItem(null);

    try {
      // Update only the two swapped items
      await Promise.all([
        handleSequence(draggedItem._id, targetSequence),
        handleSequence(draggedOverItem._id, draggedSequence)
      ]);
    } catch (error) {
      toast.error(error?.response?.data?.message || t('messages.failedUpdateSequence'));
      // Revert if update fails
      fetchImages(false);
    }
  };

  const handleDragEnd = (e) => {
    // Restore the original element's opacity
    if (e.target.style) {
      e.target.style.opacity = '1';
    }
    setDraggedItem(null);
    setDragOverItem(null);
  };

  const handleSequence = async (id, sequence) => {
    try {
      const response = await updateSequence(id, sequence);
      if (response.status === 200 || response.status === 201) {
        // toast.success(response?.data?.message || "Sequence updated successfully");
      }
    } catch (error) {
      toast.error(error?.data?.message || t('messages.somethingWentWrong'));
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold mb-4">{t('home.title')}</h2>

      <div className="bg-white rounded-3xl shadow">
        {/* Search and Add Section */}
        <div className="flex justify-between items-center p-6 pt-7">
          <div className="relative w-1/3">
            {/* <span className={`absolute inset-y-0 ${isRTL ? 'right-3' : 'left-3'} flex items-center text-black`}>
              <FiSearch size={22} />
            </span>
            <input
              type="text"
              placeholder={t('forms.searchPlaceholder')}
              className={`bg-[#F7F7F9] rounded-xl ${isRTL ? 'pr-10 pl-3' : 'pl-10 pr-3'} py-2.5 w-full outline-none`}
              dir={isRTL ? 'rtl' : 'ltr'}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
            /> */}
          </div>
          <button
            onClick={() => openModal("add")}
            className="flex items-center gap-2 bg-[#0D6EFD] text-white font-medium px-6 py-2  border-0 rounded-xl hover:bg-blue-700"
          >
            <LuPlus size={22} /> {t('home.addAdvertisement')}
          </button>
        </div>

        {/* Table */}
        {loading && (
          <div className="flex justify-center items-center h-full p-10">
            <LoadingSpinner />
          </div>
        )}
        {!loading && images.length === 0 && (
          <div className="flex justify-center items-center h-full p-10">
            <p className="text-gray-500 font-semibold">{t('home.noImages')}</p>
          </div>
        )}
        {!loading && images.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead className="bg-[#FAFAFA]">
                <tr className="text-sm font-[600]">
                  <th className={`p-2 py-4 border-b border-[#F7F7F9] ${isRTL ? 'pr-8' : 'pl-8'}`}>{t('home.id')}</th>
                  <th className="p-2 py-4 border-b border-[#F7F7F9]">{t('forms.image')}</th>
                  <th className="p-2 py-4 border-b border-[#F7F7F9]">{t('home.url')}</th>
                  <th className="p-2 py-4 border-b border-[#F7F7F9]">{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                  {images.map((image, index) => (
                    <tr
                      key={image._id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`hover:bg-gray-50 text-sm font-[500] select-none ${
                        draggedItem?._id === image._id ? "opacity-50 bg-gray-100 shadow-lg" : ""
                      } ${
                        dragOverItem?._id === image._id ? "border-t-2 border-blue-500 bg-blue-50" : ""
                      }`}
                      style={{
                        cursor: 'move',
                        transition: 'all 0.3s ease',
                        userSelect: 'none'
                      }}
                    >
                      <td className={`p-2 border-b py-6 ${isRTL ? 'pr-8' : 'pl-8'}`}>
                        <div className="flex items-center gap-2">
                          <FaGripLines className="text-gray-400" />
                          {formatNum(index + 1)}
                        </div>
                      </td>
                      <td className="p-2 border-b py-6">
                        <img src={image?.image} alt="cover" className="w-10 h-10 rounded-full object-cover" />
                      </td>
                      <td className="p-2 border-b py-6">
                        {image?.url || "-"}
                      </td>
                      <td className="p-2 border-b py-6">
                        <div className="flex gap-4 items-center">
                          <BiEditAlt
                            size={24}
                            className="cursor-pointer hover:opacity-50"
                            onClick={() => openModal("edit", image)}
                          />
                          <FaRegTrashCan
                            size={20}
                            className="cursor-pointer hover:opacity-50"
                            onClick={() => handleDelete(image._id)}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {/* {!loading && images.length > 0 && (
          <div className="flex justify-between items-center p-6 border-t border-[#F7F7F9] text-sm text-gray-600">
            <span>
              {t('pagination.showing')} {formatNum((currentPage - 1) * itemsPerPage + 1)} {t('pagination.to')} {
                formatNum(Math.min(currentPage * itemsPerPage, images.length))
              } {t('pagination.of')} {formatNum(images.length)} {t('pagination.entries')}
            </span>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )} */}
      </div>

      {/* Modal */}
      <dialog
        id="image_modal"
        className="modal shadow-[0px_1px_3px_0px_#0A0D121A]"
      >
        <div className="modal-box min-w-[800px] rounded-2xl p-0">
          <div className="p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="font-semibold text-lg">
                {modalMode === "add" ? t('home.addImage') : t('home.editImage')}
              </h3>
              <form method="dialog">
                <button className="text-xl hover:text-red-500">
                  <IoClose />
                </button>
              </form>
            </div>

            {/* Cover Upload */}
            <div className="my-5">
              <label className="block text-sm font-medium mb-2">
                {modalMode === "edit" ? t('home.currentImage') : t('home.uploadImage')} <span className="text-red-500">*</span>
              </label>
              
              {/* Show current image in edit mode */}
              {modalMode === "edit" && !coverImage && coverImageUrl && (
                <div 
                  className="mb-4 p-4 border border-gray-200 rounded-xl bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors"
                  onClick={() => document.getElementById("coverUploadInput").click()}
                >
                  {/* <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Current Image:</span>
                    <span className="text-sm text-blue-600 hover:text-blue-800 font-medium">
                      Click anywhere to change
                    </span>
                  </div> */}
                  <img
                    src={coverImageUrl}
                    alt="current cover"
                    className="max-h-32 mx-auto object-contain rounded-lg"
                  />
                  <p className="mt-2 text-center text-sm text-gray-600">
                    {getFileName(coverImageUrl)}
                  </p>
                </div>
              )}
              
              {/* Upload area */}
              <div
                className={`relative w-full border-dashed border-2 border-gray-300 p-6 text-center rounded-xl cursor-pointer hover:bg-gray-50 ${
                  modalMode === "edit" && !coverImage && coverImageUrl ? "hidden" : ""
                }`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const file = e.dataTransfer.files[0];
                  if (file && file.type.startsWith("image/")) {
                    if (file.size > 25 * 1024 * 1024) {
                      toast.error(t('home.imageTooLarge'));
                      return;
                    }
                    const imageUrl = URL.createObjectURL(file);
                    setCoverImage(file);
                    setCoverImageUrl(imageUrl);
                    // Clear image error when image is selected
                    setErrors(prev => ({ ...prev, image: "" }));
                  } else if (file && !file.type.startsWith("image/")) {
                    toast.error(t('home.invalidImageFile'));
                  }
                }}
                onClick={() => document.getElementById("coverUploadInput").click()}
              >
                {coverImageUrl && coverImage ? (
                  <div>
                    <img
                      src={coverImageUrl}
                      alt="cover"
                      className="max-h-40 mx-auto mb-2 object-contain"
                    />
                  </div>
                ) : (
                  <div className="flex flex-col items-center text-gray-500">
                    <div className="bg-gray-100 rounded-full min-h-12 min-w-12 flex items-center justify-center">
                      <RiImageAddLine size={25} className="text-blue-500" />
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <p className="text-md text-blue-500 font-medium">{t('home.clickToUpload')}</p>
                      <p className="text-md text-black">{t('home.dragAndDrop')}</p>
                    </div>
                    <p className="text-sm text-black">
                      {t('home.acceptedFormats')}
                    </p>
                  </div>
                )}
                <input
                  id="coverUploadInput"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      if (!file.type.startsWith("image/")) {
                        toast.error(t('home.invalidImageFile'));
                        return;
                      }
                      if (file.size > 25 * 1024 * 1024) {
                        toast.error(t('home.imageTooLarge'));
                        return;
                      }
                      const imageUrl = URL.createObjectURL(file);
                      setCoverImage(file);
                      setCoverImageUrl(imageUrl);
                      // Clear image error when image is selected
                      setErrors(prev => ({ ...prev, image: "" }));
                    }
                  }}
                  onClick={(e) => {
                    e.target.value = '';
                  }}
                />
              </div>
              
              {/* Show file name below upload preview */}
              {coverImage && (
                <div className="mt-2 text-center">
                  <p className="text-sm text-gray-600">
                    {t('home.selectedFile')}: {getFileName(coverImage)}
                  </p>
                  <p className="text-xs text-gray-500">
                    {t('section.size')}: {formatNum((coverImage.size / (1024 * 1024)).toFixed(2))} MB
                  </p>
                </div>
              )}

              {/* Show inline error for image */}
              {errors.image && <p className="text-red-500 absolute mt-[2px] text-xs">{errors.image}</p>}
            </div>

            {/* Link Builder */}
            <div className="my-6">
              <label className="block text-sm font-medium mb-2">
                {t('forms.navigationLink') || 'Navigation Link'}
              </label>
              <InternalLinkBuilder
                value={imageUrl}
                onChange={handleLinkChange}
                name="url"
                error=""
              />
            </div>
          </div>

          {/* Submit */}
          <div className="modal-action bg-[#fafafa] mt-6 p-6 py-4 justify-end flex">
            <button
              disabled={loadingAddForm || loading}
              type="button"
              onClick={modalMode === "add" ? handleSubmit : handleUpdate}
              className="btn bg-[#0D6EFD] rounded-xl p-2 px-5 text-white hover:bg-blue-700"
            >
              {loadingAddForm ? (
                <LoadingSpinner />
              ) : modalMode === "add" ? (
                t('common.add')
              ) : (
                t('common.update')
              )}
            </button>
          </div>
        </div>
      </dialog>
    </div>
  );
}
